/**
 * Background - Creates a layered, atmospheric background
 * Part of the "Tactile Minimalism" aesthetic with Morning Fog palette
 */

import Phaser from 'phaser';

/**
 * Configuration for background
 */
export interface BackgroundConfig {
  /** Top gradient color (hex string) */
  topColor: string;
  /** Bottom gradient color (hex string) */
  bottomColor: string;
  /** Enable vignette effect */
  enableVignette: boolean;
  /** Vignette intensity (0-1) */
  vignetteIntensity: number;
  /** Vignette start distance from center (0-1) */
  vignetteStart: number;
}

export const DEFAULT_BACKGROUND_CONFIG: BackgroundConfig = {
  topColor: '#E8ECF2',
  bottomColor: '#D8DDE4',
  enableVignette: true,
  vignetteIntensity: 0.1,
  vignetteStart: 0.6,
};

/**
 * Creates an atmospheric layered background with gradient and vignette
 */
export class Background {
  private scene: Phaser.Scene;
  private config: BackgroundConfig;
  private gradientGraphics: Phaser.GameObjects.Graphics | null = null;
  private vignetteGraphics: Phaser.GameObjects.Graphics | null = null;

  constructor(scene: Phaser.Scene, config: Partial<BackgroundConfig> = {}) {
    this.scene = scene;
    this.config = { ...DEFAULT_BACKGROUND_CONFIG, ...config };
    this.create();
  }

  /**
   * Create the background layers
   */
  private create(): void {
    const { width, height } = this.scene.scale;

    this.createGradient(width, height);

    if (this.config.enableVignette) {
      this.createVignette(width, height);
    }
  }

  /**
   * Create the gradient background
   */
  private createGradient(width: number, height: number): void {
    this.gradientGraphics = this.scene.add.graphics();
    this.gradientGraphics.setDepth(0);

    const topColor = Phaser.Display.Color.HexStringToColor(this.config.topColor);
    const bottomColor = Phaser.Display.Color.HexStringToColor(this.config.bottomColor);

    // Draw gradient line by line
    for (let y = 0; y < height; y++) {
      const ratio = y / height;
      const color = Phaser.Display.Color.Interpolate.ColorWithColor(
        topColor,
        bottomColor,
        100,
        ratio * 100
      );

      this.gradientGraphics.fillStyle(
        Phaser.Display.Color.GetColor(color.r, color.g, color.b),
        1
      );
      this.gradientGraphics.fillRect(0, y, width, 1);
    }
  }

  /**
   * Create the vignette effect
   */
  private createVignette(width: number, height: number): void {
    this.vignetteGraphics = this.scene.add.graphics();
    this.vignetteGraphics.setDepth(998); // Just below paper texture

    // Corner darkening
    // Top-left corner
    this.drawCornerGradient(0, 0, 1, 1);
    // Top-right corner
    this.drawCornerGradient(width, 0, -1, 1);
    // Bottom-left corner
    this.drawCornerGradient(0, height, 1, -1);
    // Bottom-right corner
    this.drawCornerGradient(width, height, -1, -1);

    // Edge gradients
    this.drawEdgeGradients(width, height);
  }

  /**
   * Draw corner shadow gradient
   */
  private drawCornerGradient(
    startX: number,
    startY: number,
    dirX: number,
    dirY: number
  ): void {
    if (!this.vignetteGraphics) return;

    const cornerSize = 150;
    const steps = 8;

    for (let i = 0; i < steps; i++) {
      const t = i / steps;
      const size = cornerSize * (1 - t);
      const alpha = this.config.vignetteIntensity * Math.pow(1 - t, 3) * 0.3;

      this.vignetteGraphics.fillStyle(0x000000, alpha);
      this.vignetteGraphics.fillCircle(
        startX + (dirX * size) / 4,
        startY + (dirY * size) / 4,
        size
      );
    }
  }

  /**
   * Draw edge gradients for vignette
   */
  private drawEdgeGradients(width: number, height: number): void {
    if (!this.vignetteGraphics) return;

    const edgeSize = 60;
    const steps = 5;

    for (let i = 0; i < steps; i++) {
      const t = i / steps;
      const alpha = this.config.vignetteIntensity * 0.15 * (1 - t);

      this.vignetteGraphics.fillStyle(0x000000, alpha);

      // Top edge
      this.vignetteGraphics.fillRect(0, 0, width, edgeSize * (1 - t));
      // Bottom edge
      this.vignetteGraphics.fillRect(0, height - edgeSize * (1 - t), width, edgeSize * (1 - t));
      // Left edge
      this.vignetteGraphics.fillRect(0, 0, edgeSize * (1 - t), height);
      // Right edge
      this.vignetteGraphics.fillRect(width - edgeSize * (1 - t), 0, edgeSize * (1 - t), height);
    }
  }

  /**
   * Set the depth of background layers
   */
  setDepth(gradientDepth: number, vignetteDepth: number): void {
    if (this.gradientGraphics) {
      this.gradientGraphics.setDepth(gradientDepth);
    }
    if (this.vignetteGraphics) {
      this.vignetteGraphics.setDepth(vignetteDepth);
    }
  }

  /**
   * Destroy background elements
   */
  destroy(): void {
    if (this.gradientGraphics) {
      this.gradientGraphics.destroy();
      this.gradientGraphics = null;
    }
    if (this.vignetteGraphics) {
      this.vignetteGraphics.destroy();
      this.vignetteGraphics = null;
    }
  }
}
